import { Box, Stack, Typography, styled } from '@mui/material'
import { type KeyboardEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

import { useLineInbox } from './useLineInbox'

/**
 * sidebar IconBar active='messages' 時の context pane = 患者との LINE のやり取り。
 *
 * 行を押すと本文 (/messages/:threadId) にそのやり取りが出て、一覧はここに残る。
 * 未読を上から順に読んでいけるように、患者のドロワーへは飛ばさない (design-book ADR-0052)。
 */

// 文字サイズモード追従 (ADR-0019): サイドバーに載る文字は幅と一緒に拡大する。
const Section = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  fontSize: scaledFontSize('bodySm')(theme),
  color: colorTokens.fg.default,
}))

const Heading = styled(Typography)(({ theme }) => ({
  color: colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  fontWeight: FONT_WEIGHT.medium,
  marginBottom: 8,
}))

// empty-states 型 1 (明示プレースホルダ)。一覧の終端も同じ体裁 (KnowledgePane と同じ)。
const EmptyRow = styled(Box)(({ theme }) => ({
  padding: '5px 8px',
  fontSize: scaledFontSize('tag')(theme),
  color: colorTokens.fg.muted,
}))

const TRUNCATED_LABEL = 'これより前のやり取りは表示していません'

// 行は患者を指すデータ行なので、患者リストの行と同じ字格にする (nav の行より 1 段大きい)。
const Row = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})<{ active: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  backgroundColor: active ? colorTokens.bg.muted : undefined,
  '&:hover': active ? undefined : { backgroundColor: colorTokens.bg.muted },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: colorTokens.focus.ringNeutral,
  },
}))

// 未読ドット: 8px・fg.default の塗り。既読は描かず場所だけ取って名前の x を揃える。
// 状態ドットの outline (= これから / 対象外) と読み違えないよう、既読を輪郭で描かない。
const UnreadDot = ({ unread }: { unread: boolean }) => (
  <Box
    component="span"
    data-testid={unread ? 'thread-unread-dot' : undefined}
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: unread ? colorTokens.fg.default : 'transparent',
    }}
  />
)

// 今日は HH:MM、それより前は M/D
export const formatThreadTime = (iso: string, now: Date = new Date()) => {
  const d = new Date(iso)
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(
      d.getMinutes()
    ).padStart(2, '0')}`
  }
  return `${d.getMonth() + 1}/${d.getDate()}`
}

interface Props {
  onNavigate?: () => void
}

export const MessagesPane = ({ onNavigate }: Props) => {
  const navigate = useNavigate()
  const { threadId } = useParams<{ threadId?: string }>()
  const { threads, isLoading, hasMore } = useLineInbox()

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  return (
    <Section sx={{ pt: 1.5, overflow: 'auto' }}>
      <Heading>LINE のやり取り</Heading>
      {isLoading ? null : threads.length > 0 ? (
        <Stack spacing={0.25}>
          {threads.map((thread) => {
            const path = `/messages/${thread.id}`
            const time = thread.last_message?.sent_at ?? thread.updated_at
            return (
              <Row
                key={thread.id}
                active={thread.id === threadId}
                role="button"
                tabIndex={0}
                aria-current={thread.id === threadId ? 'true' : undefined}
                onClick={() => go(path)}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()
                  go(path)
                }}
              >
                <UnreadDot unread={thread.unread_count > 0} />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: scaledFontSize('label'),
                    fontWeight: FONT_WEIGHT.medium,
                    color: colorTokens.fg.default,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {thread.patient_name}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: scaledFontSize('tag'),
                    color: colorTokens.fg.faint,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatThreadTime(time)}
                </Typography>
              </Row>
            )
          })}
          {hasMore ? <EmptyRow>{TRUNCATED_LABEL}</EmptyRow> : null}
        </Stack>
      ) : (
        <EmptyRow>(なし)</EmptyRow>
      )}
    </Section>
  )
}
